<script setup>
import { ref, shallowRef } from 'vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { fullName } from '@/utils/names';

useSessionGuard();

const headers = [
  { title: 'Name', key: 'lname' },
  { title: 'User type', key: 'usertype_desc' },
  { title: 'Role', key: 'user_roleID' },
];
const roleName = (id) => (id == 1 ? 'Admin' : id != null ? 'Staff' : '');

const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/user-details/getUsers'),
);
load();

// Change type dialog. The role only applies to user type 1 (staff side).
const userTypes = shallowRef([]);
apiRequest('get', '/user-type/getAllUsertype')
  .then((rows) => (userTypes.value = (rows || []).map((t) => ({ ...t, id: String(t.id) }))))
  .catch(() => {});

const fields = [
  { key: 'usertypeID', label: 'User type', type: 'autocomplete', required: true, items: userTypes, itemTitle: 'description', itemValue: 'id' },
  {
    key: 'user_roleID',
    label: 'Role',
    type: 'select',
    required: true,
    items: [
      { title: 'Admin', value: 1 },
      { title: 'Staff', value: 2 },
    ],
    hidden: (m) => m.usertypeID != 1,
  },
];
const editing = shallowRef(null);
const editOpen = shallowRef(false);
const form = ref({ usertypeID: null, user_roleID: null });
const { busy, run } = useAsyncAction();

function edit(user) {
  editing.value = user;
  form.value = {
    usertypeID: user.usertypeID != null ? String(user.usertypeID) : null,
    user_roleID: user.user_roleID ?? null,
  };
  editOpen.value = true;
}

async function save() {
  const ok = await run(
    () =>
      apiRequest('post', '/user-details/updateUserTypeRole', {
        id: editing.value.id,
        usertypeID: form.value.usertypeID,
        user_roleID: form.value.usertypeID == 2 ? null : form.value.user_roleID,
      }),
    { success: 'User updated.', error: 'Could not update the user.' },
  );
  if (ok) {
    editOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="Users"
    description="Every account and the user type it belongs to."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search users"
    empty-text="No users found."
    :row-label="fullName"
    :row-actions="(user) => [{ title: 'Change type', icon: 'mdi-pencil-outline', onClick: () => edit(user) }]"
  >
    <template #[`item.lname`]="{ item }">
      <span class="font-weight-medium">{{ fullName(item) }}</span>
    </template>
    <template #[`item.user_roleID`]="{ item }">
      <UiBadge v-if="roleName(item.user_roleID)" :variant="item.user_roleID == 1 ? 'outline' : 'secondary'">
        {{ roleName(item.user_roleID) }}
      </UiBadge>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="editOpen"
    title="Change user type"
    :description="fullName(editing)"
    :fields="fields"
    v-model:values="form"
    :saving="busy"
    submit-text="Save changes"
    @submit="save"
  />
</template>
